/**
 * Pure helpers behind the Exécutions pages (F2): run-list filtering, the
 * GOV.UK-style error summary of a run, durations and localized timestamps.
 * No React in here — everything is unit-testable with plain fixtures.
 */
import type { JobState, JobSummary, StepState } from "./api";
import { categoryOf, type StateCategory } from "./states";

/** The categories offered by the run-list filter ("all" = no filter). */
export const RUN_FILTER_CATEGORIES = [
  "all",
  "running",
  "waiting",
  "success",
  "warning",
  "failure",
] as const;

export type RunFilterCategory = (typeof RUN_FILTER_CATEGORIES)[number];

export function isRunFilterCategory(value: string): value is RunFilterCategory {
  return (RUN_FILTER_CATEGORIES as readonly string[]).includes(value);
}

/**
 * Client-side filter over the fetched job list: state category (via the
 * shared state map) + free-text search over dag name and job id.
 */
export function filterJobs<T extends JobSummary>(
  jobs: T[],
  filter: { category?: RunFilterCategory; search?: string },
): T[] {
  const category = filter.category ?? "all";
  const needle = (filter.search ?? "").trim().toLowerCase();
  return jobs.filter((job) => {
    if (category !== "all" && categoryOf(job.state) !== category) return false;
    if (!needle) return true;
    return (
      job.dag_name.toLowerCase().includes(needle) ||
      job.job_id.toLowerCase().includes(needle)
    );
  });
}

/** Sum /api/stats `by_state` counts into per-category totals. */
export function countByCategory(
  byState: Record<string, number>,
): Record<StateCategory, number> {
  const totals: Record<StateCategory, number> = {
    queued: 0,
    running: 0,
    waiting: 0,
    success: 0,
    warning: 0,
    failure: 0,
  };
  for (const [state, count] of Object.entries(byState)) {
    const category = categoryOf(state);
    if (category) totals[category] += count;
  }
  return totals;
}

/** RFC-4180 quoting: wrap fields containing separators/quotes/newlines. */
export function csvField(value: string): string {
  return /[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/**
 * Audit-trail CSV of the (already filtered) run list — client-side, from
 * the fetched jobs. CRLF line endings so Excel opens it cleanly.
 */
export function jobsToCsv(jobs: JobSummary[]): string {
  const header = ["job_id", "pipeline", "state", "dashboard_status", "created_at"];
  const lines = [
    header.join(","),
    ...jobs.map((job) =>
      [job.job_id, job.dag_name, job.state, job.dashboard_status, job.created_at ?? ""]
        .map(csvField)
        .join(","),
    ),
  ];
  return lines.join("\r\n") + "\r\n";
}

/** DOM id for a step row — the error summary anchors (scrolls) to it. */
export function stepAnchorId(stepKey: string): string {
  return `run-step-${stepKey.replace(/[^A-Za-z0-9_-]/g, "-")}`;
}

/**
 * Steps that belong in the error summary: failed steps always; on a
 * COMPLETED_WITH_CONTROLS run, also the steps whose controls flagged rows
 * (unmatched) or that carry an error detail.
 */
export function summarySteps(job: Pick<JobState, "state" | "steps">): StepState[] {
  const jobCategory = categoryOf(job.state);
  return job.steps.filter((step) => {
    if (categoryOf(step.state) === "failure") return true;
    if (jobCategory === "warning") {
      return step.unmatched_rows > 0 || Boolean(step.error_detail);
    }
    return false;
  });
}

/** Milliseconds between two ISO timestamps; null when either is missing/bad. */
export function stepDurationMs(
  startedAt: string | null | undefined,
  finishedAt: string | null | undefined,
): number | null {
  if (!startedAt || !finishedAt) return null;
  const start = Date.parse(startedAt);
  const end = Date.parse(finishedAt);
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return null;
  return end - start;
}

/** Compact human duration: "<1 s", "42 s", "3 min 05 s", "2 h 07 min". */
export function formatDuration(ms: number): string {
  if (ms < 1000) return "<1 s";
  const totalSeconds = Math.round(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds} s`;
  const totalMinutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (totalMinutes < 60) {
    return `${totalMinutes} min ${String(seconds).padStart(2, "0")} s`;
  }
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours} h ${String(minutes).padStart(2, "0")} min`;
}

/** Localized date-time ("23/08/2026 14:05" fr / "23/08/2026, 14:05" en). */
export function formatDateTime(
  iso: string | null | undefined,
  language: string,
): string {
  if (!iso) return "—";
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) return iso;
  const locale = language.startsWith("fr") ? "fr-FR" : "en-GB";
  return new Date(parsed).toLocaleString(locale, {
    dateStyle: "short",
    timeStyle: "short",
  });
}

/** Localized date-time to the second ("23/08/2026 14:05:37"): an upload's precise time. */
export function formatDateTimeSeconds(
  iso: string | null | undefined,
  language: string,
): string {
  if (!iso) return "—";
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) return iso;
  const locale = language.startsWith("fr") ? "fr-FR" : "en-GB";
  return new Date(parsed).toLocaleString(locale, {
    dateStyle: "short",
    timeStyle: "medium",
  });
}

/** The run list's date filter: a year, a month, a day — any combination. */
export type DateFilter = { year?: number; month?: number; day?: number };

/** How many years the Year choice offers, the current one included. */
export const DATE_FILTER_YEARS = 5;

/** Read `y`, `m`, `d` from the URL; anything that is not a sane number is ignored. */
export function parseDateFilter(params: URLSearchParams): DateFilter {
  const read = (key: string, min: number, max: number): number | undefined => {
    const raw = params.get(key);
    if (raw == null || raw === "") return undefined;
    const value = Number(raw);
    return Number.isInteger(value) && value >= min && value <= max ? value : undefined;
  };
  return {
    year: read("y", 1900, 2999),
    month: read("m", 1, 12),
    day: read("d", 1, 31),
  };
}

/**
 * Fill the parts a partial choice leaves out: a month without a year means
 * this year, a day without a month means this month (of this year).
 */
export function completeDateFilter(filter: DateFilter, now = new Date()): DateFilter {
  const { year, month, day } = filter;
  if (day != null) {
    return { year: year ?? now.getFullYear(), month: month ?? now.getMonth() + 1, day };
  }
  if (month != null) return { year: year ?? now.getFullYear(), month };
  return year != null ? { year } : {};
}

export function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

/**
 * The local-time window [from, to) a date choice covers, as ISO instants for
 * the API; null when nothing is chosen. A day past the end of its month is
 * clamped to the last day.
 */
export function dateWindow(
  filter: DateFilter,
  now = new Date(),
): { from: string; to: string } | null {
  const full = completeDateFilter(filter, now);
  if (full.year == null) return null;
  let start: Date;
  let end: Date;
  if (full.month != null && full.day != null) {
    const day = Math.min(full.day, daysInMonth(full.year, full.month));
    start = new Date(full.year, full.month - 1, day);
    end = new Date(full.year, full.month - 1, day + 1);
  } else if (full.month != null) {
    start = new Date(full.year, full.month - 1, 1);
    end = new Date(full.year, full.month, 1);
  } else {
    start = new Date(full.year, 0, 1);
    end = new Date(full.year + 1, 0, 1);
  }
  return { from: start.toISOString(), to: end.toISOString() };
}

/** The years the Year choice offers: the last few, plus the one chosen. */
export function filterYears(chosen: number | undefined, now = new Date()): number[] {
  const current = now.getFullYear();
  const years = Array.from({ length: DATE_FILTER_YEARS }, (_, i) => current - i);
  if (chosen != null && !years.includes(chosen)) years.push(chosen);
  return years.sort((a, b) => b - a);
}
